/**
 * Persistent gRPC call-history store.
 *
 * Records gRPC calls made anywhere in the webapp (the dev-tools tester *and*
 * ordinary website traffic) so they can be reviewed and — for SupportService
 * calls — replayed from the dev-tools form. Entries are recorded by the
 * logging interceptor in `api/connect-web-setup.ts` while the "Log gRPC to
 * console" toggle is enabled, and persisted in localStorage so they survive
 * reloads. Only the most-recent `MAX_HISTORY_ITEMS` calls are kept.
 *
 * A small in-process subscription lets the dev-tools History tab update live as
 * calls come in (the browser `storage` event only fires cross-tab, not for the
 * tab doing the writing).
 *
 * This module is browser-safe and dependency-free so it can be imported from
 * both the API layer and the dev-tools page without creating a layering cycle.
 */

export type CallHistorySource = "dev-tools" | "website";

/**
 * dev-tools marks the request message instances it sends so the logging
 * interceptor can tag their history source without adding a network header
 * (a new custom header would trigger a CORS preflight the edge router may
 * reject — breaking the call with "Failed to fetch"). The WeakSet lets marked
 * request objects be garbage-collected normally.
 */
const devToolsRequestMarks = new WeakSet<object>();

export const markDevToolsRequest = (message: object): void => {
  devToolsRequestMarks.add(message);
};

export const isDevToolsRequest = (message: unknown): boolean =>
  typeof message === "object" &&
  message !== null &&
  devToolsRequestMarks.has(message);

export interface CallHistoryEntry {
  /** Stable list key. */
  id: string;
  /** ISO timestamp of when the call completed. */
  timestamp: string;
  /** Where the call originated. */
  source: CallHistorySource;
  /** Fully-qualified proto service name, e.g. "s12.tools.support.v1.SupportService". */
  serviceTypeName: string;
  /** camelCase method (localName) — matches the dev-tools method keys. */
  methodName: string;
  /** Region header (`x-region`) at call time. */
  region?: string;
  /** Reference id header (`sc-reference`) at call time. */
  referenceId?: string;
  /** JSON-safe request payload — the source of truth for replay. */
  request: unknown;
  success: boolean;
  durationMs?: number;
  /**
   * Normalized, JSON-safe result for the Response tab. Shaped like a
   * dev-tools `MethodInvocationResult` (`{ success, data, error, duration }`)
   * so the existing response viewer renders it unchanged.
   */
  result: unknown;
  error?: string;
  /** User-pinned favourite. Pinned entries survive the cap and "Clear history". */
  pinned?: boolean;
}

const STORAGE_KEY = "sc:devtools:call-history";

/** Keep only the top X most-recent calls. */
export const MAX_HISTORY_ITEMS = 100;

/** Responses larger than this (serialized) are dropped to protect the quota. */
const MAX_RESULT_BYTES = 20_000;

let idCounter = 0;

/** Generate a unique entry id (Date.now alone collides under rapid traffic). */
export const nextHistoryId = (methodName: string): string =>
  `${Date.now()}-${idCounter++}-${methodName}`;

type Listener = (entries: CallHistoryEntry[]) => void;

/**
 * Change notifications go through a window CustomEvent rather than an in-module
 * listener set. The bundler can load this module as more than one instance
 * (e.g. the API chunk and a lazy dev-tools chunk), and a per-module Set would
 * leave the interceptor notifying a different set than the one the hook
 * subscribed to — history would only appear after a reload. `window` is a true
 * global, so dispatch and listen always meet. Mirrors the nav-redesign toggle.
 */
const HISTORY_EVENT = "sc:devtools:grpc-history-changed";

const notify = (entries: CallHistoryEntry[]): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<CallHistoryEntry[]>(HISTORY_EVENT, { detail: entries }),
  );
};

/**
 * Subscribe to history changes. Returns an unsubscribe function.
 *
 * Two listeners cover both cases: the CustomEvent updates other components in
 * the *same* tab, and the `storage` event updates *other* tabs/windows (it
 * only fires in documents that didn't make the write) — so a call recorded in
 * one tab appears live in another.
 */
export const subscribeHistory = (listener: Listener): (() => void) => {
  // Matches notify()'s guard: this store is browser-only, but staying
  // window-safe keeps it usable from any non-DOM context (SSR, node tooling).
  if (typeof window === "undefined") return () => {};
  const customHandler = (event: Event): void => {
    listener(
      (event as CustomEvent<CallHistoryEntry[]>).detail ?? loadHistory(),
    );
  };
  const storageHandler = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY || event.key === null) {
      listener(loadHistory());
    }
  };
  window.addEventListener(HISTORY_EVENT, customHandler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(HISTORY_EVENT, customHandler);
    window.removeEventListener("storage", storageHandler);
  };
};

/**
 * Recursively convert a value into something `JSON.stringify` can round-trip.
 * protobuf-es message instances expose `toJson()`; Dates become ISO strings.
 */
export const serializeResult = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;
  if (value instanceof Date) return value.toISOString();

  const maybeMessage = value as { toJson?: () => unknown };
  if (typeof maybeMessage.toJson === "function") {
    try {
      return maybeMessage.toJson();
    } catch {
      // Fall through to structural serialization.
    }
  }

  if (Array.isArray(value)) return value.map(serializeResult);

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = serializeResult(val);
    }
    return out;
  }

  return value;
};

/**
 * Serialize a request message for storage/replay. Enums are emitted as their
 * integer values (`enumAsInteger`) — the dev-tools form and protobuf-es both
 * use numeric enum values, so a name string (toJson's default) would fail to
 * match the dropdown options and fail to re-encode on replay.
 */
export const serializeRequestMessage = (message: unknown): unknown => {
  const m = message as {
    toJson?: (options?: { enumAsInteger?: boolean }) => unknown;
  };
  if (m && typeof m.toJson === "function") {
    try {
      return m.toJson({ enumAsInteger: true });
    } catch {
      // Fall through to structural serialization.
    }
  }
  return serializeResult(message);
};

/** Replace an oversized or non-serializable result with a small marker. */
const capResultSize = (entry: CallHistoryEntry): CallHistoryEntry => {
  try {
    const json = JSON.stringify(entry.result);
    if (json && json.length > MAX_RESULT_BYTES) {
      return { ...entry, result: { note: "[response too large to store]" } };
    }
    return entry;
  } catch {
    return { ...entry, result: { note: "[response not serializable]" } };
  }
};

/** Persist the list, retrying with a trimmed list if the quota is exceeded. */
const persist = (list: CallHistoryEntry[]): CallHistoryEntry[] => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return list;
  } catch {
    const trimmed = list.slice(0, Math.max(1, Math.floor(list.length / 2)));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
      return trimmed;
    } catch {
      // eslint-disable-next-line no-console
      console.error("Failed to persist gRPC call history");
      return list;
    }
  }
};

/**
 * Cap the list to the most-recent `MAX_HISTORY_ITEMS` — but never evict a
 * pinned entry. Pinned favourites are kept regardless of position; the cap only
 * bounds the unpinned tail. Assumes a newest-first list, so the kept unpinned
 * entries are the most recent.
 */
const capHistory = (list: CallHistoryEntry[]): CallHistoryEntry[] => {
  let unpinnedKept = 0;
  return list.filter((entry) => {
    if (entry.pinned) return true;
    unpinnedKept += 1;
    return unpinnedKept <= MAX_HISTORY_ITEMS;
  });
};

export const loadHistory = (): CallHistoryEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return capHistory(parsed as CallHistoryEntry[]);
  } catch {
    return [];
  }
};

/**
 * Prepend an entry (newest-first), cap to the most-recent X (pinned exempt),
 * persist, and notify subscribers. Returns the resulting list.
 */
export const addHistoryEntry = (
  entry: CallHistoryEntry,
): CallHistoryEntry[] => {
  const next = capHistory([capResultSize(entry), ...loadHistory()]);
  const stored = persist(next);
  notify(stored);
  return stored;
};

/** Toggle the pinned flag on an entry, persist, and notify. Returns the list. */
export const togglePin = (id: string): CallHistoryEntry[] => {
  const next = loadHistory().map((entry) =>
    entry.id === id ? { ...entry, pinned: !entry.pinned } : entry,
  );
  const stored = persist(next);
  notify(stored);
  return stored;
};

/**
 * Clear the history but keep pinned favourites — a pin is an explicit "keep
 * this", so it survives a clear. Unpin an entry to make it clearable.
 */
export const clearHistory = (): CallHistoryEntry[] => {
  const pinned = loadHistory().filter((entry) => entry.pinned);
  if (pinned.length === 0) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage errors (e.g. Safari private mode).
    }
    notify([]);
    return [];
  }
  const stored = persist(pinned);
  notify(stored);
  return stored;
};
